/*
  Lior Apple layer: motion for the studio sections
  (lior-apple-tiles, lior-apple-brands, lior-apple-bigtype).
  - [data-lst-reveal]   plays its entrance once, when it first comes into view.
  - [data-lst-parallax] gets --p (0 → 1) while it travels through the viewport.
  - [data-lst-scrub]    gets --p (0 → 1) while its sticky stage is pinned.
  - [data-lst-light]    gets --mx / --my under the mouse for the glass highlight.
  - [data-lst-date]     shows today's date, as on the App Store's Today tab.
  - .lat-card           sinks under the finger; its glass capsule swells and glows
                        where it is touched; both spring back on release.
  - [data-lat-expand]   a tap opens the card to the whole screen while the next
                        page loads (the App Store's card opening).
  One requestAnimationFrame loop, only while something visible needs it.
  Nothing runs in the theme editor's entrance pass or with reduced motion.
*/
(function () {
  'use strict';

  if (window.__liorStudio) return;
  window.__liorStudio = true;

  var doc = document;
  var win = window;
  var mq = function (q) { return win.matchMedia ? win.matchMedia(q) : { matches: false }; };
  var reduce = mq('(prefers-reduced-motion: reduce)');
  var fine = mq('(hover: hover) and (pointer: fine)');
  var designMode = !!(win.Shopify && win.Shopify.designMode);

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* ------------------------------------------------------------------ *
   * Frame loop: read every rect first, then write
   * ------------------------------------------------------------------ */
  var visible = new Set();
  var queued = false;

  function request() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(tick);
  }

  function tick() {
    queued = false;
    if (reduce.matches) return;
    var vh = win.innerHeight || doc.documentElement.clientHeight;
    var reads = [];
    visible.forEach(function (el) {
      var r = el.getBoundingClientRect();
      var p;
      if (el.__lstMode === 'scrub') {
        var travel = el.offsetHeight - vh;
        p = travel > 0 ? clamp(-r.top / travel, 0, 1) : clamp((vh - r.top) / (vh + r.height), 0, 1);
      } else {
        p = clamp((vh - r.top) / (vh + r.height), 0, 1);
      }
      reads.push([el, Math.round(p * 1000) / 1000]);
    });
    for (var i = 0; i < reads.length; i++) {
      var el = reads[i][0];
      var v = reads[i][1];
      if (el.__lstP !== v) {
        el.__lstP = v;
        el.style.setProperty('--p', String(v));
      }
    }
  }

  var io = 'IntersectionObserver' in win ? new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      if (e.isIntersecting) visible.add(e.target);
      else visible.delete(e.target);
    }
    request();
  }, { rootMargin: '10% 0px' }) : null;

  function track(el, mode) {
    if (el.__lstTracked) return;
    el.__lstTracked = true;
    el.__lstMode = mode;
    if (io) io.observe(el);
  }

  /* ------------------------------------------------------------------ *
   * Entrance, once
   * ------------------------------------------------------------------ */
  function reveal(root) {
    if (root.__lstRevealed) return;
    root.__lstRevealed = true;
    if (designMode || reduce.matches || !io) return;
    var r = root.getBoundingClientRect();
    var vh = win.innerHeight || doc.documentElement.clientHeight;
    if (r.top < vh * 0.9 && r.bottom > 0) return; /* already on screen: no flash */
    root.classList.add('lst-anim');
    var once = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        once.disconnect();
        root.classList.add('lst-in');
        setTimeout(function () { root.classList.remove('lst-anim', 'lst-in'); }, 2600);
        break;
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    once.observe(root);
  }

  /* ------------------------------------------------------------------ *
   * Glass light under the mouse
   * ------------------------------------------------------------------ */
  var lightEl = null;
  doc.addEventListener('pointermove', function (e) {
    if (!fine.matches || e.pointerType === 'touch') return;
    var t = e.target && e.target.closest ? e.target.closest('[data-lst-light]') : null;
    if (lightEl && lightEl !== t) {
      lightEl.style.removeProperty('--mx');
      lightEl.style.removeProperty('--my');
    }
    lightEl = t;
    if (!t) return;
    var r = t.getBoundingClientRect();
    t.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
    t.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
  }, { passive: true });

  /* ------------------------------------------------------------------ *
   * Today's date over the headline
   * ------------------------------------------------------------------ */
  function dates(scope) {
    var els = (scope || doc).querySelectorAll('[data-lst-date]');
    if (!els.length) return;
    var text = '';
    try {
      var lang = (doc.documentElement.getAttribute('lang') || 'he').toLowerCase();
      text = new Intl.DateTimeFormat(lang.indexOf('he') === 0 ? 'he-IL' : lang, { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
    } catch (e) {
      text = '';
    }
    for (var i = 0; i < els.length; i++) els[i].textContent = text;
  }

  /* ------------------------------------------------------------------ *
   * Press: the card sinks at once under the finger; on its glass capsule
   * the glass swells and glows where it is touched. Both spring back on
   * release (CSS), and a quick tap still shows for a beat.
   * ------------------------------------------------------------------ */
  var press = null;

  function glow(pill, e) {
    var r = pill.getBoundingClientRect();
    pill.style.setProperty('--gx', (e.clientX - r.left).toFixed(1) + 'px');
    pill.style.setProperty('--gy', (e.clientY - r.top).toFixed(1) + 'px');
  }

  function pressEnd(now) {
    var p = press;
    if (!p) return;
    press = null;
    var go = function () {
      p.card.classList.remove('is-pressed');
      if (p.pill) p.pill.classList.remove('is-touch');
    };
    var held = Date.now() - p.t;
    if (now || held >= 90) go();
    else setTimeout(go, 90 - held);
  }

  doc.addEventListener('pointerdown', function (e) {
    if (e.button !== 0 || !e.target || !e.target.closest) return;
    var card = e.target.closest('.lat-card');
    if (!card) return;
    pressEnd(true);
    var pill = e.target.closest('.lat-pill');
    press = { card: card, pill: pill, x: e.clientX, y: e.clientY, t: Date.now() };
    card.classList.add('is-pressed');
    if (pill) {
      glow(pill, e);
      pill.classList.add('is-touch');
    }
  }, { passive: true });

  doc.addEventListener('pointermove', function (e) {
    if (!press) return;
    /* ~10px of hysteresis: a scroll that starts on a card is not a press */
    if (Math.abs(e.clientX - press.x) > 10 || Math.abs(e.clientY - press.y) > 10) pressEnd(true);
    else if (press.pill) glow(press.pill, e);
  }, { passive: true });

  doc.addEventListener('pointerup', function () { pressEnd(false); }, { passive: true });
  doc.addEventListener('pointercancel', function () { pressEnd(true); }, { passive: true });
  win.addEventListener('scroll', function () { if (press) pressEnd(true); }, { passive: true });

  /* ------------------------------------------------------------------ *
   * The App Store opening: the tapped card grows to fill the screen while
   * the next page loads. The photo keeps its crop and slides up to the top
   * edge; below it the page turns white, like an opened story. The link is
   * never held back, so this costs no time.
   * ------------------------------------------------------------------ */
  var zoom = null;

  /* A spring with a hint of bounce (damping ratio 0.86, response 0.5 s), as CSS linear() */
  var spring = (function () {
    var zeta = 0.86;
    var w = 2 * Math.PI / 0.5;
    var wd = w * Math.sqrt(1 - zeta * zeta);
    var x = function (t) { return 1 - Math.exp(-zeta * w * t) * (Math.cos(wd * t) + (zeta * w / wd) * Math.sin(wd * t)); };
    var T = 0.1;
    while (T < 2 && Math.abs(1 - x(T)) > 0.001) T += 0.01;
    var pts = [];
    for (var i = 0; i <= 40; i++) pts.push(+x(T * i / 40).toFixed(4));
    pts[40] = 1;
    var ok = false;
    try { ok = !!(win.CSS && CSS.supports('transition-timing-function', 'linear(0, 1)')); } catch (e) { ok = false; }
    return ok ? { easing: 'linear(' + pts.join(', ') + ')', duration: Math.round(T * 1000) } : { easing: 'cubic-bezier(0.32, 0.72, 0, 1)', duration: 560 };
  })();

  function samePage(a) {
    try {
      var u = new URL(a.href, location.href);
      return u.origin === location.origin && u.pathname === location.pathname && u.search === location.search;
    } catch (e) {
      return true;
    }
  }

  function collapse(instant) {
    var z = zoom;
    if (!z) return;
    zoom = null;
    clearTimeout(z.timer);
    var done = function () {
      if (z.el.parentNode) z.el.parentNode.removeChild(z.el);
      z.card.style.visibility = '';
    };
    if (instant || reduce.matches) {
      done();
      return;
    }
    var left = z.anims.length;
    z.anims.forEach(function (a) {
      a.onfinish = function () { if (--left === 0) done(); };
      a.reverse();
    });
  }

  function expand(card) {
    var r = card.getBoundingClientRect();
    var vw = doc.documentElement.clientWidth || win.innerWidth;
    var vh = win.innerHeight;
    if (!r.width || !r.height || r.bottom < 0 || r.top > vh || !doc.body.animate) return;
    collapse(true);

    var radius = parseFloat(win.getComputedStyle(card).borderTopLeftRadius) || 28;
    var root = card.closest('.lst');
    var el = doc.createElement('div');
    el.className = 'lst lat-zoom';
    el.setAttribute('aria-hidden', 'true');
    if (root) el.style.setProperty('--lat-glass', win.getComputedStyle(root).getPropertyValue('--lat-glass'));

    var sheet = doc.createElement('div');
    sheet.className = 'lat-zoom-sheet';
    var box = doc.createElement('div');
    box.className = 'lat-zoom-card';
    box.style.left = r.left + 'px';
    box.style.top = r.top + 'px';
    box.style.width = r.width + 'px';
    box.style.height = r.height + 'px';
    box.style.setProperty('--p', card.style.getPropertyValue('--p') || '0.5');
    box.innerHTML = card.innerHTML;

    /* The very same pixels the card shows: same file, same zoom */
    var src = card.querySelector('.lat-media img');
    var dst = box.querySelector('.lat-media img');
    if (src && dst) {
      dst.removeAttribute('srcset');
      dst.removeAttribute('sizes');
      dst.setAttribute('loading', 'eager');
      dst.src = src.currentSrc || src.src;
      var sc = win.getComputedStyle(src).scale;
      if (sc && sc !== 'none') dst.style.scale = sc;
    }

    sheet.appendChild(box);
    el.appendChild(sheet);
    doc.body.appendChild(el);
    card.style.visibility = 'hidden';

    var s = vw / r.width;
    var from = 'inset(' + r.top + 'px ' + (vw - r.right) + 'px ' + (vh - r.bottom) + 'px ' + r.left + 'px round ' + radius + 'px)';
    var opts = { duration: spring.duration, easing: spring.easing, fill: 'forwards' };
    var anims = [
      sheet.animate([{ clipPath: from }, { clipPath: 'inset(0px 0px 0px 0px round 0px)' }], opts),
      box.animate([{ transform: 'none' }, { transform: 'translate(' + (-r.left) + 'px, ' + (-r.top) + 'px) scale(' + s + ')' }], opts)
    ];
    box.querySelectorAll('.lat-glass, .lat-bar, .lat-rim').forEach(function (part) {
      anims.push(part.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 220, easing: 'ease-out', fill: 'forwards' }));
    });

    zoom = { el: el, card: card, anims: anims };
    /* If no page comes (the link was handled elsewhere), close it again */
    zoom.timer = setTimeout(function () {
      if (zoom && zoom.card === card && doc.visibilityState === 'visible') collapse(false);
    }, 6000);
  }

  /* Last in line, so anything that handles the click itself has had its say */
  win.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (designMode || reduce.matches) return;
    var card = e.target && e.target.closest ? e.target.closest('a.lat-card[href]') : null;
    if (!card || !card.closest('[data-lat-expand]')) return;
    if ((card.target && card.target !== '_self') || card.hasAttribute('download') || samePage(card)) return;
    expand(card);
  });

  /* Leaving: drop the copy so the page sits in the back/forward cache as it was;
     coming back from that cache: make sure nothing is left over */
  win.addEventListener('pagehide', function () { collapse(true); });
  win.addEventListener('pageshow', function (e) { if (e.persisted) collapse(true); });

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */
  function scan(scope) {
    var s = scope || doc;
    s.querySelectorAll('[data-lst-reveal]').forEach(reveal);
    s.querySelectorAll('[data-lst-parallax]').forEach(function (el) { track(el, 'parallax'); });
    s.querySelectorAll('[data-lst-scrub]').forEach(function (el) { track(el, 'scrub'); });
    dates(s);
    request();
  }

  win.addEventListener('scroll', request, { passive: true });
  win.addEventListener('resize', request, { passive: true });

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();

  doc.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
